// HUD: turns the match state into what the React HUD shows (features/game/game-store.ts): players and scores,
// phase, ball on, hints, and the toasts for shots, a found match and a finished practice frame.
import {GameStore, HudPlayer, HudState, Toast} from "../features/game/game-store";
import {FrameState, objectBallsOnTable, redsOnTable, RULESET_NAMES, rulesetOf, ShotSummary} from "../shared/rules";
import type {MatchState, OnlineInfo} from "./types";
import type {MatchFoundMsg} from "./messages";

/** Arguments for GameStore.toast: text, kind, and how long it stays (ms, default 4500). */
export type ToastArgs = [text: string, kind: Toast["kind"], ms?: number];

export const BALL_IN_HAND_HINT = "Ball in hand: use W A S D to place the cue ball in the D, then play your shot.";

type HudView = Pick<MatchState, "mode" | "phase" | "online" | "frame" | "practiceName">;

/** The HUD fields that come from the match (everything but hint, toasts, searching, connection, pointerLocked). */
export function hudState(m: HudView): Partial<HudState> {
    const f = m.frame;
    let players: HudPlayer[];
    if (m.mode === "online" && m.online) {
        players = m.online.players.map((p, i) => ({
            name: p.username, guest: p.guest, connected: p.connected,
            score: f.stats[i]?.score ?? 0, highestBreak: f.stats[i]?.highestBreak ?? 0,
        }));
    } else {
        players = [{name: m.practiceName, guest: false, connected: true, score: f.stats[0].score, highestBreak: f.stats[0].highestBreak}];
    }
    return {
        mode: m.mode,
        phase: m.phase,
        gameId: m.online?.gameId ?? null,
        you: m.online?.you ?? 0,
        turn: f.turn,
        players,
        breakPoints: f.breakPoints,
        ballOn: f.ballOn,
        ballInHand: f.ballInHand,
        redsLeft: redsOnTable(f.positions),
        ballsLeft: objectBallsOnTable(f.positions),
        ruleset: rulesetOf(f),
        suddenDeath: !!f.suddenDeath,
        status: m.mode === "online" ? (m.online?.status ?? "playing") : (f.over ? "over" : "playing"),
        winner: f.winner,
        endReason: f.endReason,
        rematch: m.online?.rematch ?? [false, false],
        ranked: m.mode === "online" && !!m.online?.ranked,
        clock: m.mode === "online" && m.online?.status === "playing" ? (m.online.clock ?? null) : null,
        elo: m.mode === "online" ? (m.online?.elo?.map(e => ({rating: e.rating, delta: e.delta})) ?? null) : null,
    };
}

export function publish(m: HudView) {
    GameStore.set(hudState(m));
}

/** The toast for a shot's result: "You: …" / "<opponent>: …" online, just the message in practice. */
export function shotToast(summary: ShotSummary, mode: MatchState["mode"], online: OnlineInfo | null): ToastArgs {
    const mine = mode === "practice" || (online && summary.player === online.you);
    const who = mode === "online" && online ? (mine ? "You" : online.players[summary.player]?.username) : "";
    const text = mode === "online" ? `${who}: ${summary.message}` : summary.message;
    return [text, summary.foul ? "foul" : summary.points > 0 ? "good" : "info"];
}

export function matchFoundToast(msg: MatchFoundMsg): ToastArgs {
    const opponent = msg.opponent?.username || "opponent";
    const rules = msg.ruleset && msg.ruleset !== "snooker" ? ` (${RULESET_NAMES[msg.ruleset] || msg.ruleset})` : "";
    const kind = msg.ranked ? "Ranked match" : "Match";
    return [msg.rematch ? `Rematch against ${opponent}${rules}!` : `${kind} found${rules}: you vs ${opponent}. ${msg.yourTurn ? "You break." : `${opponent} breaks.`}`, "good", 6000];
}

/** Seconds left on a shot clock (rounded up, never below 0). */
export function clockSeconds(deadline: number, now: number): number {
    return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** "+12" / "-8" / "±0" for an Elo change. */
export function formatEloDelta(delta: number): string {
    return delta > 0 ? `+${delta}` : delta < 0 ? `-${-delta}` : "\u00b10";
}

/** Practice frame finished. */
export function frameCompleteToast(state: FrameState): ToastArgs {
    if (rulesetOf(state) === "anyball") return [`Table cleared: ${state.stats[0].score} points, best run ${state.stats[0].highestBreak}.`, "good", 8000];
    return [`Frame complete: ${state.stats[0].score} points, highest break ${state.stats[0].highestBreak}.`, "good", 8000];
}

/**
 * The hint after a new frame state: the ball-in-hand hint while we place the cue ball, cleared when it no longer
 * applies; undefined = leave the hint as it is (some other hint, or none).
 */
export function hintAfterFrame(ballInHandNow: boolean, current: string | null): string | null | undefined {
    if (ballInHandNow) return BALL_IN_HAND_HINT;
    if (current && current.startsWith("Ball in hand")) return null;
    return undefined;
}

/** socket "match-found" */
export function onMatchFound(msg: MatchFoundMsg) {
    GameStore.set({searching: false});
    GameStore.toast(...matchFoundToast(msg));
}
